import * as THREE from "three";
import debug from "debug";
import AssetLoader from "./AssetLoader";

const log = debug("app:glb");

class AssetVault {
  static instance;

  constructor(experience) {
    if (AssetVault.instance) {
      return AssetVault.instance;
    }
    AssetVault.instance = this;

    this.experience = experience;
    this.eventHub = this.experience.eventHub;
    this.assets = new Map();
    this.loadingRequests = new Map(); // Tracks ongoing asset loads as Promises

    this.assetLoader = new AssetLoader();

    // Controlled loading
    this.maxConcurrentLoads = 10;
    this.loadQueue = [];
    this.activeLoads = 0;
  }

  static getInstance(experience) {
    if (!AssetVault.instance) {
      AssetVault.instance = new AssetVault(experience);
    }
    return AssetVault.instance;
  }

  cleanup() {
    this.assets.clear();
    this.loadingRequests.clear();
    this.loadQueue = [];
    this.activeLoads = 0;
    this.assetLoader = null;
    
    log("AssetVault cleanup complete: All assets and pending requests cleared.");
  }

  /**
   * Fetch asset or load if not present
   * Returns a Promise that resolves with a clone or a rejection object
   * @category Experience
   * @subcategory Utils
   */
  async getAsset(key) {
    if (!key) throw new Error("Key cannot be empty.");

    // Check if already in sceneObjects (shallow clone)
    if (this.assets.has(key)) {
      log("this.assets.has", key);
      return this._getShallowCopy(key);
    }

    // Check if the asset is already being loaded and wait for it to finish properly
    if (this.loadingRequests.has(key)) {
      try {
        await Promise.race([
          this.loadingRequests.get(key),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Loading timeout")), 60000)
          ),
        ]);
        if (this.assets.has(key)) {
          return this._getShallowCopy(key);
        }
        throw new Error("Asset not added to sceneObjects after loading.");
      } catch (error) {
        throw error;
      }
    }

    // Lock and load asset, only resolving the Promise after storing in assetsPersistent
    this.loadingRequests.set(
      key,
      new Promise(async (resolve, reject) => {
        try {
          const { asset } = await this._loadAsset(key);
          resolve(); // Resolve only after saving to assetsPersistent
        } catch (error) {
          reject(error);
        } finally {
          this.loadingRequests.delete(key); // Ensure lock is released
        }
      })
    );

    // Wait for the locked request to finish loading
    await this.loadingRequests.get(key);

    // Return a deep clone of the loaded object
    if (!this.assets.has(key)) {
      throw new Error("Asset not available after loading.");
    }
    log(" return this.assetsPersistent.get(key).clone();", key);
    return this._getShallowCopy(key);
  }

  _getShallowCopy(key) {
    const shape = this.assets.get(key);
    const shallowCopy = new THREE.Object3D().copy(shape, true);
    return shallowCopy;
  }

  /**
   * Private method to handle actual asset loading
   */
  async _loadAsset(key) {
    return new Promise(async (resolve, reject) => {
      try {
        // ✅ Fetch the presigned URL from the backend
        const urls = await this.experience.dataProvider.getPresignedUrlsForParts([key]);
        const presignedUrl = urls?.[0];
  
        if (!presignedUrl) {
          throw new Error(`No presigned URL received for ${key}`);
        }
  
        log(`Loading asset from presigned URL: ${presignedUrl}`);
        const { asset, error } = await this.assetLoader.loadAsset({
          key,
          url: presignedUrl,
        });
  
        if (error) {
          reject({ asset: null, error });
        } else {
          this.assets.set(key, asset); // Cache the loaded asset
          resolve({ asset, error: null });
        }
      } catch (error) {
        reject({
          asset: null,
          error: `Asset loading failed for ${key}: ${error.message}`,
        });
      } finally {
        this.loadingRequests.delete(key);
      }
    });
  }
  

  /**
   * Bulk loading multiple assets
   */
  async getAssets(keys) {
    const results = await Promise.all(
      keys.map((key) => this.getAsset(key).catch((error) => error))
    );

    return results.map((result) =>
      result.asset ? result : { asset: null, error: "Failed to load asset" }
    );
  }

  /**
   * Clears the assets map (for debugging or complete state reset).
   * Also clears loadingRequests to prevent stale in-flight loads from repopulating.
   */
  destroy() {
    this.assets.clear();
    this.loadingRequests.clear();
    log("All assets and loading requests cleared from the vault.");
  }
}
export default AssetVault;
